import React, { useState, useEffect, useRef } from 'react';
import { MedicationRequestDialog } from '../components/medications/MedicationRequestDialog';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, InputBase, IconButton, Skeleton, Snackbar, Alert } from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/axios';
import { useDebounce } from '../hooks/useDebounce';
import { MedicationCard, PharmacyProductItem } from '../components/medications/MedicationCard';
import { MedicationCartProvider, useMedicationCart } from '../contexts/MedicationCartContext';
import { CartFab } from '../components/medications/CartFab';
import { CartDrawer } from '../components/medications/CartDrawer';

const RECENT_KEY = 'med-recent-searches';
const MAX_RECENT = 5;

function getRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveRecent(term: string): void {
  try {
    const prev = getRecent().filter((t) => t !== term);
    localStorage.setItem(RECENT_KEY, JSON.stringify([term, ...prev].slice(0, MAX_RECENT)));
  } catch {
    // ignore
  }
}

const SkeletonCard: React.FC = () => (
  <Box
    sx={{
      background: 'white',
      borderRadius: '14px',
      padding: '14px 16px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      borderLeft: '4px solid #E1E8ED',
    }}
  >
    <Skeleton variant="text" width="60%" height={18} />
    <Skeleton variant="text" width="40%" height={14} sx={{ mt: 0.5 }} />
    <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
      <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: '6px' }} />
      <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: '6px' }} />
    </Box>
  </Box>
);

const MedicationsLookupContent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(() => searchParams.get('q') ?? '');
  const [focused, setFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const { items, addItem } = useMedicationCart();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestMedName, setRequestMedName] = useState('');
  const [requestProductId, setRequestProductId] = useState<string | undefined>(undefined);
  const [requestSnackbar, setRequestSnackbar] = useState(false);

  const debouncedTerm = useDebounce(inputValue.trim(), 300);
  const searchTerm = debouncedTerm.length >= 2 ? debouncedTerm : '';

  useEffect(() => {
    setRecentSearches(getRecent());
  }, []);

  useEffect(() => {
    if (searchTerm) {
      saveRecent(searchTerm);
      setRecentSearches(getRecent());
    }
  }, [searchTerm]);

  const { data, isLoading: isSearching, isError: isSearchError } = useQuery<{
    results: PharmacyProductItem[];
    total: number;
  }>({
    queryKey: ['pharmacy-products-search', searchTerm],
    queryFn: async () => {
      const { data } = await apiClient.get('/pharmacy-products/search', {
        params: { q: searchTerm },
      });
      return data;
    },
    enabled: searchTerm.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const products = data?.results;

  const cartIds = new Set(items.map((i) => i.pharmacyProductId));

  const handleAdd = (product: PharmacyProductItem) => {
    const added = addItem({
      pharmacyProductId: product.id,
      name: product.name,
      chainName: product.chainName,
      price: '0',
      quantity: 1,
    });
    setSnackbar({
      open: true,
      message: added ? `${product.name} agregado` : 'Ya está en el carrito',
      severity: added ? 'success' : 'info',
    });
  };

  const handleRecentTap = (term: string) => {
    setInputValue(term);
    inputRef.current?.blur();
    setFocused(false);
  };

  const handleClear = () => {
    setInputValue('');
    inputRef.current?.focus();
  };

  const showRecent = focused && inputValue === '' && recentSearches.length > 0;
  const showEmpty = !searchTerm && !showRecent;
  const showSkeleton = isSearching && searchTerm.length > 0;
  const showError = searchTerm.length > 0 && isSearchError;
  const showResults = searchTerm.length > 0 && !isSearching && !isSearchError && !!products && products.length > 0;
  const showNoResults = searchTerm.length > 0 && !isSearching && !isSearchError && !!products && products.length === 0;

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: '#F0F5FF',
        mx: -2,
        mt: -2,
        px: 2,
        pt: 2,
      }}
    >
      {/* Search bar */}
      <Box
        sx={{
          background: 'white',
          border: focused ? '2px solid #0066CC' : '2px solid #E1E8ED',
          borderRadius: '50px',
          px: 2,
          py: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: focused ? '0 4px 20px rgba(0,102,204,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'border-color 150ms, box-shadow 150ms',
          mb: 1,
        }}
      >
        <SearchIcon sx={{ color: focused ? '#0066CC' : '#A0AEC0', fontSize: 20, flexShrink: 0 }} />
        <InputBase
          inputRef={inputRef}
          fullWidth
          placeholder="Buscar medicamento..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          sx={{ fontSize: 14, py: 1.25, color: '#14171A' }}
          inputProps={{ 'aria-label': 'buscar medicamento' }}
        />
        {inputValue && (
          <IconButton size="small" onClick={handleClear} sx={{ p: 0.5, color: '#A0AEC0' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Result count */}
      {showResults && (
        <Typography sx={{ fontSize: 11, color: '#A0AEC0', mb: 1, ml: 0.5 }}>
          {products!.length} resultado{products!.length !== 1 ? 's' : ''} encontrado{products!.length !== 1 ? 's' : ''}
        </Typography>
      )}

      {/* Recent searches */}
      {showRecent && (
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '1px',
              color: '#A0AEC0',
              textTransform: 'uppercase',
              mb: 1,
              ml: 0.5,
            }}
          >
            Búsquedas recientes
          </Typography>
          {recentSearches.map((term) => (
            <Box
              key={term}
              onClick={() => handleRecentTap(term)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
                px: 0.5,
                borderBottom: '1px solid #F0F5FF',
                cursor: 'pointer',
                '&:last-child': { borderBottom: 'none' },
                '&:hover': { bgcolor: 'rgba(0,102,204,0.04)', borderRadius: '8px' },
              }}
            >
              <SearchIcon sx={{ color: '#C1C9D2', fontSize: 16 }} />
              <Typography sx={{ fontSize: 13, color: '#505A66' }}>{term}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Empty state — animated logo */}
      {showEmpty && (
        <Box
          sx={{
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 0.18 },
              '50%': { transform: 'scale(1.08)', opacity: 0.28 },
            },
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-8px)' },
            },
            '@keyframes textFade': {
              '0%, 100%': { opacity: 0.12, letterSpacing: '12px' },
              '50%': { opacity: 0.22, letterSpacing: '16px' },
            },
          }}
        >
          <Box sx={{ textAlign: 'center', pt: 3, mb: 2 }}>
            <Typography variant="h3" sx={{ color: '#14171A', mb: 1 }}>
              Busca un medicamento
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#A0AEC0' }}>
              Nombre del producto o ingrediente activo
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '40vh',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                animation: 'float 4s ease-in-out infinite',
              }}
            >
              <Box sx={{ animation: 'pulse 3s ease-in-out infinite' }}>
                <img
                  src="/logo-icon.svg"
                  alt=""
                  style={{ width: 120, height: 120, opacity: 0.6, filter: 'grayscale(30%)' }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: '"Georgia", "Palatino Linotype", serif',
                  fontSize: 28,
                  fontWeight: 400,
                  color: '#0867d2',
                  mt: -1,
                  animation: 'textFade 3s ease-in-out infinite 0.5s',
                  opacity: 0.12,
                }}
              >
                LYSI
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Skeleton loading */}
      {showSkeleton && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </Box>
      )}

      {/* Search error */}
      {showError && (
        <Box sx={{ textAlign: 'center', pt: 6 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#14171A' }}>
            Error al buscar
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#A0AEC0', mt: 0.5 }}>
            Verifica tu conexión e intenta de nuevo
          </Typography>
        </Box>
      )}

      {/* No results */}
      {showNoResults && (
        <Box sx={{ textAlign: 'center', pt: 6 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#14171A' }}>
            No encontramos &quot;{searchTerm}&quot;
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#A0AEC0', mt: 0.5 }}>
            Intenta con otro nombre o término
          </Typography>
          <Typography
            onClick={() => {
              setRequestMedName(searchTerm);
              setRequestProductId(undefined);
              setRequestDialogOpen(true);
            }}
            sx={{
              fontSize: 13,
              color: '#0066CC',
              mt: 2,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Solicitar este medicamento
          </Typography>
        </Box>
      )}

      {/* Results */}
      {showResults && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pb: 10 }}>
          {products!.map((product) => (
            <MedicationCard
              key={product.id}
              product={product}
              isInCart={cartIds.has(product.id)}
              onAdd={handleAdd}
              onRequest={(p) => {
                setRequestMedName(p.name);
                setRequestProductId(p.id);
                setRequestDialogOpen(true);
              }}
            />
          ))}
        </Box>
      )}

      <CartFab onClick={() => setDrawerOpen(true)} />
      <CartDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <MedicationRequestDialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        medicationName={requestMedName}
        pharmacyProductId={requestProductId}
        onSuccess={() => setRequestSnackbar(true)}
      />

      <Snackbar
        open={requestSnackbar}
        autoHideDuration={4000}
        onClose={() => setRequestSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setRequestSnackbar(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Solicitud enviada exitosamente
        </Alert>
      </Snackbar>
    </Box>
  );
};

export const MedicationsLookup: React.FC = () => (
  <MedicationCartProvider>
    <MedicationsLookupContent />
  </MedicationCartProvider>
);
